<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';

const props = defineProps({
  ariaLabel: { type: String, default: 'Select row' },
  modelValue: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  variant: { type: String, default: '' },
  size: { type: [Number, String], default: 28 },
});

const emit = defineEmits(['update:modelValue', 'change']);

const instance = getCurrentInstance();
const vnodeProps = instance?.vnode?.props || {};
const hasModelValueProp =
  Object.prototype.hasOwnProperty.call(vnodeProps, 'modelValue') ||
  Object.prototype.hasOwnProperty.call(vnodeProps, 'model-value');

const localChecked = ref(Boolean(props.modelValue));

watch(
  () => props.modelValue,
  value => {
    if (!hasModelValueProp) return;
    localChecked.value = Boolean(value);
  }
);

const checkedValue = computed(() =>
  hasModelValueProp ? Boolean(props.modelValue) : localChecked.value
);

const checkboxStyle = computed(() => {
  if (props.size === null || props.size === undefined || props.size === '') {
    return {};
  }

  if (typeof props.size === 'number') {
    return { '--checkbox-size': `${props.size}px` };
  }

  const normalized = String(props.size).trim();
  if (/^\d+$/.test(normalized)) {
    return { '--checkbox-size': `${normalized}px` };
  }

  return { '--checkbox-size': normalized };
});

const onInputChange = event => {
  const next = event.target.checked;
  if (!hasModelValueProp) {
    localChecked.value = next;
  }
  emit('update:modelValue', next);
  emit('change', next);
};
</script>

<template>
  <label
    class="ios-checkbox"
    :class="[
      { red: variant === 'red' },
      { 'ios-checkbox--disabled': disabled },
    ]"
    :style="checkboxStyle"
    :aria-label="ariaLabel"
  >
    <input
      type="checkbox"
      :checked="checkedValue"
      :disabled="disabled"
      @change="onInputChange"
    />
    <div class="checkbox-wrapper" aria-hidden="true">
      <div class="checkbox-bg" />
      <svg class="checkbox-icon" viewBox="0 0 24 24" fill="none">
        <path
          class="check-path"
          d="M4 12L10 18L20 6"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </label>
</template>

<style scoped>
/* From Uiverse.io by Tsiangana */
.ios-checkbox {
  --checkbox-size: 28px;
  --checkbox-color: #3b82f6;
  --checkbox-bg: #dbeafe;
  --checkbox-border: #93c5fd;

  position: relative;
  display: inline-block;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.ios-checkbox input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.checkbox-wrapper {
  position: relative;
  width: var(--checkbox-size);
  height: var(--checkbox-size);
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.checkbox-bg {
  position: absolute;
  inset: 0;
  border-radius: 8px;
  border: 2px solid var(--checkbox-border);
  background: #ffffff;
  transition: all 0.2s ease;
}

.checkbox-icon {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 80%;
  height: 80%;
  color: #ffffff;
  transform: scale(0);
  transition: all 0.2s ease;
}

.check-path {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  transition: stroke-dashoffset 0.3s ease 0.1s;
}

.ios-checkbox input:checked + .checkbox-wrapper .checkbox-bg {
  background: var(--checkbox-color);
  border-color: var(--checkbox-color);
}

.ios-checkbox input:checked + .checkbox-wrapper .checkbox-icon {
  transform: scale(1);
}

.ios-checkbox input:checked + .checkbox-wrapper .check-path {
  stroke-dashoffset: 0;
}

.ios-checkbox:not(.ios-checkbox--disabled):hover .checkbox-wrapper {
  transform: scale(1.05);
}

.ios-checkbox:not(.ios-checkbox--disabled):active .checkbox-wrapper {
  transform: scale(0.95);
}

.ios-checkbox input:focus-visible + .checkbox-wrapper .checkbox-bg {
  box-shadow: 0 0 0 4px var(--checkbox-bg);
}

.ios-checkbox.red {
  --checkbox-color: #ef4444;
  --checkbox-bg: #fee2e2;
  --checkbox-border: #fca5a5;
}

.ios-checkbox--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.ios-checkbox--disabled .checkbox-wrapper {
  transform: none !important;
}

@keyframes bounce {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.ios-checkbox:not(.ios-checkbox--disabled) input:checked + .checkbox-wrapper {
  animation: bounce 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
